import { Bell } from '@phosphor-icons/react';
import { Popup, VText } from '@vake/ui-kit';
import { FC, useState } from 'react';
import { SendToMattermost } from '../pages/Report/components/send-to-mattermost';
import { useSendToMattermost } from '../requests/reports';
import { MiniSpinner, Spinner } from '../shared/styles/Spinner';

const HandleMissingPFData: FC<{
    createdAt?: number | null;
    hasImages: boolean | undefined;
    hasAIS: boolean | undefined;
    showMessage: boolean;
    setShowMessage: (value: boolean) => void;
}> = ({ createdAt, hasImages, hasAIS }) => {
    const minutesThreshold = 13;
    const date = new Date();
    const timeUTC = date.getTime(); // + date.getTimezoneOffset() * 60000; Assume UTC aware timestamp from backend
    const timeDiffMinutes = createdAt ? (timeUTC - createdAt) / (1000 * 60) : null;
    if (hasAIS && hasImages) return null;
    if (timeDiffMinutes && timeDiffMinutes < minutesThreshold) {
        // Handles if report is missing data, but it has been generated recently and could be processing
        return (
            <VText variant="H3">
                We are creating your report. <br />
                It should be ready in less than {Math.floor(minutesThreshold - timeDiffMinutes - 1)} minute
                {minutesThreshold - timeDiffMinutes - 1 === 1 ? '' : 's'}
            </VText>
        );
    }

    let h3Text = '';
    let descriptionText = '';
    if (!hasImages && !hasAIS) {
        // Something has presumeably gone wrong and error message will be displayed
        h3Text = 'Your report was not generated correctly';
        descriptionText =
            'This is typically because AIS data does not exist for this time period, or something went wrong on our end.';
    } else {
        // Want to send slackbotmessage when hasImages/hasAIS is undefined as this will mean something is not working as expected
        // The particular message text is handled in the API
        h3Text = `Your report is missing ${hasAIS ? 'images' : 'AIS'}`;
        descriptionText = `This is usually a sign of missing data for the time period or something wrong with the processing on our end.`;
    }
    return (
        <>
            <div className="flex gap-4">
                <VText variant="H3">{h3Text}</VText>
                <img style={{ height: '20px' }} src="/images/icons/satellite.svg" alt="" />
            </div>
            <VText>{descriptionText}</VText>
            <SendToMattermost
                textOnButton={'Notify VAKE'}
                iconOnButton={<Bell size={16} />}
                textToSend={`Notified VAKE button pressed: This pathfinder [report](${window.location.href}). has images: ${hasImages}, has AIS: ${hasAIS}.`}
                successMessage={"Thank you, we'll be in touch soon."}
            />
        </>
    );
};

export const PathfinderErrorHandler: FC<{
    hasAIS?: boolean;
    hasImages?: boolean;
    createdAt?: number | null;
    isLoading?: boolean;
}> = ({ hasAIS, hasImages, createdAt, isLoading }) => {
    // Handle if user want to see imperfect report
    const [showMessage, setShowMessage] = useState<boolean>(true);
    const { mutate } = useSendToMattermost();

    const handleUserToggledPopup = () => {
        setShowMessage(!showMessage);
        mutate({
            text: `User exited Pathfinder Report status popup for [report](${window.location.href}). has images: ${hasImages}, has AIS: ${hasAIS}.`,
            channel: 'user-actions',
        });
    };
    if (hasImages === true && hasAIS === true && !isLoading) return null;
    if (isLoading || showMessage)
        return (
            <Popup isOpen={showMessage} tooglePopup={handleUserToggledPopup}>
                <div className="flex flex-col gap-4">
                    <VText variant="H3">Pathfinder Report Status</VText>
                    <div>
                        <div className="flex">
                            <VText>Gathering AIS track</VText>
                            <CheckOrCrossSymbol check={hasAIS} />
                        </div>
                        <div className="flex">
                            <VText>Correlating satellite images with AIS track</VText>
                            <CheckOrCrossSymbol check={hasImages} />
                        </div>
                    </div>
                    {(!hasAIS || !hasImages) && (
                        <HandleMissingPFData
                            createdAt={createdAt}
                            hasImages={hasImages}
                            hasAIS={hasAIS}
                            showMessage={showMessage}
                            setShowMessage={setShowMessage}
                        />
                    )}
                    {isLoading && (
                        <div className="flex justify-center items-center">
                            <Spinner />
                            <VText>Loading report...</VText>
                        </div>
                    )}
                </div>
            </Popup>
        );
    return null;
};

export const CheckOrCrossSymbol: FC<{ check?: boolean }> = ({ check }) => {
    if (typeof check === 'undefined') {
        return <MiniSpinner size={20} />;
    } else if (check) {
        return <img src="/images/icons/simpleSymbols/check.svg" alt="check" />;
    } else {
        return <img src="/images/icons/simpleSymbols/x.svg" alt="check" />;
    }
};

type OverwatchErrorHandlerProps = {
    isError: boolean;
    errorMessage: string;
};
export const OverwatchErrorHandler: FC<OverwatchErrorHandlerProps> = ({ isError, errorMessage }) => {
    const [isOpen, setIsOpen] = useState<boolean>(true);
    const { mutate } = useSendToMattermost();

    const handleTooglePopup = () => {
        setIsOpen(!isOpen);
        mutate({
            text: `User exited popup for Overwatch [report](${window.location.href}). Error: ${errorMessage}`,
            channel: 'user-actions',
        });
    };
    return (
        <>
            <Popup isOpen={isError && isOpen} tooglePopup={handleTooglePopup}>
                <div className="flex flex-col ">
                    <VText variant="H3">Overwatch failed</VText>
                    <VText>Something went wrong on our end</VText>
                    <SendToMattermost
                        textOnButton={'Notify VAKE'}
                        iconOnButton={<Bell size={16} />}
                        textToSend={`Notified VAKE button pressed: The Overwatch [report](${window.location.href}) failed. Error: ${errorMessage}`}
                        successMessage={"Thank you, we'll be in touch soon."}
                    />
                </div>
            </Popup>
        </>
    );
};
